/**
 * Keeps the page, sort, filters and search term in the address bar so /management/users
 * survives a reload and can be linked to.
 *
 * The param names match what Management::UsersController reads, so the URL the
 * user sees is the same one the JSON request uses. replaceState rather than
 * pushState, mirroring lib/admin-kit/hooks/useAdminResource — paging and
 * filtering are not treated as navigation history.
 */

export const DEFAULT_SORT_ORDER = 'ASC';

// The two tabs the list is split into (PD-4677). Mirrors
// ManagementUsersQuery::CURRENT_ACCESS / REMOVED_ACCESS, which is what the
// `access` param means to the server.
export const CURRENT_ACCESS = 'current';
export const REMOVED_ACCESS = 'removed';

export const EMPTY_FILTERS = {
  accountIds: [],
  permissions: [],
  jobRoles: [],
};

// Filter key in state <-> array param the controller reads.
const FILTER_PARAMS = {
  accountIds: 'account_ids',
  permissions: 'permissions',
  jobRoles: 'job_roles',
};

export const readUrlState = () => {
  const params = new URLSearchParams(window.location.search);
  const page = parseInt(params.get('page'), 10);
  const sortBy = params.get('sort_by') || null;

  let sortOrder = null;
  if (sortBy) {
    sortOrder =
      params.get('sort_order') === 'DESC' ? 'DESC' : DEFAULT_SORT_ORDER;
  }

  const filters = { ...EMPTY_FILTERS };
  Object.entries(FILTER_PARAMS).forEach(([key, param]) => {
    filters[key] = params.getAll(`${param}[]`);
  });

  return {
    // Anything that is not the removed tab is the current one, matching the
    // server's own fallback rather than trusting the address bar.
    access:
      params.get('access') === REMOVED_ACCESS ? REMOVED_ACCESS : CURRENT_ACCESS,
    filters,
    page: Number.isInteger(page) && page > 0 ? page : 1,
    query: params.get('q') || '',
    sortBy,
    sortOrder,
  };
};

export const writeUrlState = ({
  access,
  filters,
  page,
  query,
  sortBy,
  sortOrder,
}) => {
  const params = new URLSearchParams();

  // Only the removed tab is written: current is the default, and a param
  // spelling the default would show in every URL the page ever produces.
  if (access === REMOVED_ACCESS) params.set('access', REMOVED_ACCESS);
  if (page > 1) params.set('page', page);
  if (query) params.set('q', query);
  if (sortBy) {
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder || DEFAULT_SORT_ORDER);
  }

  Object.entries(FILTER_PARAMS).forEach(([key, param]) => {
    (filters?.[key] || []).forEach((value) =>
      params.append(`${param}[]`, value),
    );
  });

  const search = params.toString();

  // The hash is carried over rather than dropped: rewriting the URL on every
  // page, sort or filter change would otherwise silently discard any fragment
  // the user arrived with.
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${
      window.location.hash
    }`,
  );
};
